import React, { useEffect, useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';

/**
 * Presentation-only adapter for any serializable attack-feature decision. A decision may also carry a `selection`
 * (Improved Cunning Strike): options to toggle, up to `max` and within a `budget` of their `cost`s, confirmed as one
 * choice `<prefix><id>+<id>`. Or a `grid` (Smite): `rows` by `columns`, where each cell names one of the decision's
 * choices (or is disabled, or null where the row doesn't apply); those choices then leave the footer. Or `cards`
 * (Unarmed Strike): choices shown as a grid of cards, each `{ choiceId, label, detail, Icon, primary }`; they too leave
 * the footer.
 */
export default function FeatureDecisionModal({ decision, onChoose }) {
  const [picked, setPicked] = useState([]);
  useEffect(() => { setPicked([]); }, [decision?.id]);
  const dismiss = () => {
    if (decision) onChoose({ decisionId: decision.id, choiceId: 'skip' });
  };
  const selection = decision?.selection;
  const cost = (ids) => ids.reduce((sum, id) => sum + (Number(selection?.options.find((option) => option.id === id)?.cost) || 0), 0);
  const left = selection ? selection.budget - cost(picked) : 0;
  const toggle = (id) => setPicked((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  const grid = decision?.grid;
  const cards = decision?.cards;
  const inGrid = new Set([...(grid?.rows || []).flatMap((row) => Object.values(row.cells || {}).map((cell) => cell?.choiceId).filter(Boolean)),
    ...(cards || []).map((card) => card.choiceId)]);

  return (
    <Modal
      className="dnd-modal modern-modal feature-decision-modal"
      dialogClassName={`feature-decision-modal__dialog${decision?.grid ? ' feature-decision-modal__dialog--wide' : ''}`}
      centered
      show={Boolean(decision)}
      onHide={dismiss}
    >
      <Modal.Header closeButton>
        <Modal.Title>{decision?.title || decision?.label || 'Feature decision'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {decision?.detail && <p>{decision.detail}</p>}
        {decision?.prompt && <p>{decision.prompt}</p>}
        {cards && (
          <div className="feature-decision-cards" role="group" aria-label={`${decision.title || 'Feature'} options`}>
            {cards.map(({ choiceId, label, detail, Icon, primary, disabled }) => (
              <button key={choiceId} type="button" disabled={disabled} aria-label={detail ? `${label}: ${detail}` : label}
                className={`feature-decision-card${primary ? ' feature-decision-card--primary' : ''}`}
                onClick={() => onChoose({ decisionId: decision.id, choiceId })}>
                {Icon && <span className="feature-decision-card__icon" aria-hidden="true"><Icon size={20} /></span>}
                <span className="feature-decision-card__text">
                  <span className="feature-decision-card__label">{label}</span>
                  {detail && <span className="feature-decision-card__detail">{detail}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
        {grid && (
          <div className="feature-decision-grid">
            <table>
              <thead>
                <tr><th scope="col"><span className="visually-hidden">Option</span></th>
                  {grid.columns.map((column) => <th key={column.key} scope="col">{column.label}<span className="feature-decision-grid__detail">{column.detail}</span></th>)}</tr>
              </thead>
              <tbody>
                {grid.rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}{row.detail && <span className="feature-decision-grid__detail">{row.detail}</span>}</th>
                    {grid.columns.map((column) => {
                      const cell = row.cells?.[column.key];
                      if (!cell) return <td key={column.key} className="feature-decision-grid__empty" aria-label="Not available at this level">—</td>;
                      return (
                        <td key={column.key}>
                          <Button type="button" size="sm" variant={cell.choiceId ? 'primary' : 'outline-secondary'} disabled={!cell.choiceId || cell.disabled}
                            aria-label={cell.ariaLabel || cell.label} title={cell.detail || undefined}
                            onClick={() => onChoose({ decisionId: decision.id, choiceId: cell.choiceId })}>{cell.label}</Button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {selection && (
          <>
            <div role="group" aria-label={`${decision.title || 'Feature'} options`} className="d-grid gap-2">
              {selection.options.map((option) => {
                const chosen = picked.includes(option.id);
                const unavailable = option.disabled || (!chosen && (picked.length >= selection.max || Number(option.cost) > left));
                return (
                  <Card key={option.id} as="button" type="button" role="checkbox" aria-checked={chosen} disabled={unavailable}
                    className={`text-start selectable-card feature-decision-option${chosen ? ' feature-decision-option--selected' : ''}`}
                    onClick={() => toggle(option.id)}>
                    <Card.Body className="py-2">
                      <strong>{option.label}</strong> · {option.cost}{selection.unit || ''}
                      {option.disabled && option.detail && <div className="small">{option.detail}</div>}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
            <p className="small mt-2 mb-0">{`Up to ${selection.max} · ${left}${selection.unit || ''} left`}</p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {(decision?.choices || []).filter((choice) => !inGrid.has(choice.id)).map((choice) => (
          <Button key={choice.id} type="button" disabled={choice.disabled} title={choice.detail || undefined} variant={choice.variant || (choice.action === 'apply' || choice.id === 'apply' ? 'primary' : 'secondary')}
            onClick={() => onChoose({ decisionId: decision.id, choiceId: choice.id })}>
            {choice.label}
          </Button>
        ))}
        {selection && (
          <Button type="button" variant="primary" disabled={!picked.length}
            onClick={() => onChoose({ decisionId: decision.id, choiceId: `${selection.prefix}${picked.join('+')}` })}>
            {selection.confirmLabel || 'Confirm'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
